// Anhaenge an Aktivitaeten (Anruf, Termin, Notiz) und Aufgaben.
//
// Die Datei liegt wie ueberall als `Documents`-Zeile plus S3-Objekt und wird ueber
// dieselbe Lambda-Kette hoch- und wieder heruntergeladen (helpers/api/document.js).
// Die VERKNUEPFUNG steht dagegen am Datensatz selbst: `Tasks.documents` und
// `Activities.documents` sind `[Documents]` OHNE @hasMany — jede Verbindung erzeugt
// eigene Resolver, und die API ist am Ressourcenlimit von CloudFormation. Ohne
// Direktive liest nichts die Documents-Tabelle: gespeichert wird eine schlanke Kopie
// (id, name, fileType, type), die der Verlauf ohne zweite Abfrage mitbekommt.
//
// Deshalb `slim()` unten: nur diese vier Felder schreiben. Alles andere waere eine
// zweite Wahrheit neben der Documents-Zeile, und `country`/`countries` tragen dort
// Field-Level-@auth, deren Resolver ueber ein eingebettetes Objekt nichts zu suchen
// haben.
//
// Eigene Collection, damit die Anhaenge von Aktivitaeten nicht an denselben Rechten
// haengen wie die von Kommentaren. Die Berechtigungen heissen
// `document.<collectionName>.<type>.<action>` und stehen als Freitext am NUTZER in der
// Datenbank (Rolle -> permissions) — die Presigned-URL-Lambda baut den String aus der
// Documents-Zeile und prueft ihn serverseitig, fuer create, read UND delete. Ein
// Umbenennen hier verlangt also, dass
//   document.activities.other.create | .read | .delete
// in jeder Rolle liegt, die Anhaenge hochladen bzw. sehen soll — sonst 401.
//
// ponytail: die Zugriffsgrenze bleibt die Collection, nicht der Datensatz. Ceiling: wer
// das Recht hat, laedt JEDEN Anhang JEDER Aktivitaet per docId, auch an einer Notiz,
// die er ueber AppSync nicht lesen darf — die Lambda schaut den Elterndatensatz nicht
// an (kein objectID-Lookup). Upgrade-Pfad: in der Lambda fuer diese Collection die
// Aktivitaet ueber Documents.objectID nachlesen und userId/companyOwnerUserId/leadShare
// gegen claims.sub halten.
import {
  deleteDocument,
  trackUploadsProgress,
  uploadDocument,
} from "../helpers/api/document";

export const ACTIVITY_DOC_COLLECTION = "activities";
export const ACTIVITY_DOC_TYPE = "other";
export const ACTIVITY_DOC_PATH = "documents/activity";

/** Die Anhaenge eines Datensatzes in der Form, die CompactDocumentUpload erwartet. */
export const documentsOf = (record) => record?.documents || [];

// Nur diese vier Felder wandern in den Datensatz — mehr braucht weder das Anzeigen
// noch das Herunterladen, und mehr waere eine zweite Wahrheit neben `Documents`.
const slim = (doc) => ({
  id: doc.id,
  name: doc.name,
  fileType: doc.fileType,
  type: doc.type || ACTIVITY_DOC_TYPE,
});

/**
 * Anhaenge eines Formulars verarbeiten und die Liste zurueckgeben, die in
 * `documents` der Aufgabe bzw. Aktivitaet gehoert.
 *
 * `docs` ist die Liste aus CompactDocumentUpload — Neues traegt `file`, Bestehendes
 * `id`, und zum Loeschen Markiertes zusaetzlich `delete: true`.
 *
 * Laeuft VOR der Mutation: die Datei entsteht zuerst, ihre ID kommt mit dem
 * Datensatz in einem Zug in die Datenbank. Ohne `objectID` an der `Documents`-Zeile —
 * die Verknuepfung ist das Feld am Datensatz, nicht der Fremdschluessel.
 */
export const saveActivityDocuments = async ({ docs, companyId, country }) => {
  const rows = docs || [];
  const toUpload = rows.filter((doc) => !doc.id && doc.file);

  // Derselbe Fortschritts-Toast wie in den uebrigen Upload-Dialogen. Nur wenn
  // wirklich etwas hochgeht — sonst blinkt bei jedem Speichern ein leerer Balken.
  const tracker =
    toUpload.length > 0
      ? trackUploadsProgress(toUpload.length, "Documents")
      : null;

  let created;
  try {
    created = await Promise.all(
      toUpload.map((doc, i) =>
        uploadDocument(
          {
            companyId: companyId || "",
            collectionName: ACTIVITY_DOC_COLLECTION,
            name: doc.name,
            type: ACTIVITY_DOC_TYPE,
            fileType: doc.fileType,
            storageUrl: `${ACTIVITY_DOC_PATH}/${companyId || "unassigned"}`,
          },
          doc.file,
          country,
          tracker ? { onProgress: tracker.onProgressFor(i) } : undefined,
        ),
      ),
    );
    tracker?.finish();
  } catch (err) {
    tracker?.fail(err?.message);
    throw err;
  }

  // Ein fehlgeschlagenes Loeschen darf das Speichern nicht zuruecknehmen — die Datei
  // bleibt dann in S3 stehen, das ist die harmlosere Haelfte.
  await Promise.all(
    rows
      .filter((doc) => doc.id && doc.delete)
      .map((doc) =>
        deleteDocument(doc.id).catch((err) =>
          console.error("Anhang konnte nicht geloescht werden", doc.id, err),
        ),
      ),
  );

  return [
    ...rows.filter((doc) => doc.id && !doc.delete).map(slim),
    ...created.map(slim),
  ];
};
